import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Users, 
  BookOpen, 
  Award, 
  TrendingUp, 
  Shield, 
  Database,
  UserCheck,
  BarChart3,
  Clock,
  AlertCircle
} from 'lucide-react';
import { useAdminDashboard } from '@/hooks/useAdminDashboard';
import { adminChartsDataService } from '@/services/adminChartsDataService';
import UserRoleDistributionChart from './charts/UserRoleDistributionChart';
import CourseStatusDistributionChart from './charts/CourseStatusDistributionChart';
import TopActiveCoursesChart from './charts/TopActiveCoursesChart';
import UserEngagementChart from './charts/UserEngagementChart';
import { useNavigate, useSearchParams } from 'react-router-dom';

const AdminOverviewTab: React.FC = () => {
  const { stats, activities, loading: statsLoading } = useAdminDashboard();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [chartsData, setChartsData] = useState({
    userRoles: { admin: 0, staff: 0, user: 0 },
    courseStatus: { notStarted: 0, inProgress: 0, completed: 0 },
    topCourses: [] as { name: string; enrollments: number }[],
    engagement: [] as { date: string; activeUsers: number }[],
  });
  const [chartsLoading, setChartsLoading] = useState(true);

  useEffect(() => {
    loadChartsData();
  }, []);

  const loadChartsData = async () => {
    setChartsLoading(true);
    try {
      const [userRoles, courseStatus, topCourses, engagement] = await Promise.all([
        adminChartsDataService.getUserRoleDistribution(),
        adminChartsDataService.getCourseStatusDistribution(),
        adminChartsDataService.getTopActiveCourses(5),
        adminChartsDataService.getUserEngagementTrend(30),
      ]);

      setChartsData({
        userRoles,
        courseStatus,
        topCourses,
        engagement,
      });
    } catch (error) {
      console.error('Error loading charts data:', error);
    } finally {
      setChartsLoading(false);
    }
  };

  const navigateToTab = (tab: string, subtab?: string) => {
    if (subtab) {
      setSearchParams({ tab, subtab });
    } else {
      setSearchParams({ tab });
    }
  };

  const getSystemHealthColor = () => {
    if (stats?.systemHealth === 'healthy') return 'bg-success';
    if (stats?.systemHealth === 'warning') return 'bg-warning';
    return 'bg-destructive';
  };

  const getSystemHealthText = () => {
    if (stats?.systemHealth === 'healthy') return 'Healthy';
    if (stats?.systemHealth === 'warning') return 'Warning';
    return 'Critical';
  };

  if (statsLoading || chartsLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-muted rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Count pending approvals from activities
  const pendingCount = activities?.filter(a => a.type === 'submission').length || 0;

  return (
    <div className="space-y-6">
      {/* System Health Card */}
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            System Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <Badge className={`${getSystemHealthColor()} mt-1`}>
                {getSystemHealthText()}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Users</p>
              <p className="text-2xl font-bold">{stats?.activeUsers || 0}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Last Backup</p>
              <p className="text-sm font-medium flex items-center gap-1 mt-1">
                <Clock className="h-4 w-4" />
                {stats?.lastBackup || 'Unknown'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-2">Storage</p>
              <Progress value={Math.min((stats?.storage.totalGB || 0) * 10, 100)} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">
                {stats?.storage.totalGB?.toFixed(2) || 0} GB ({stats?.storage.buckets?.length || 0} buckets)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigateToTab('users', 'approvals')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Approvals</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2">
              {pendingCount}
              {pendingCount > 0 && (
                <Badge variant="destructive" className="text-xs">!</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Awaiting review
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalUsers || 0}</div>
            <p className="text-xs text-muted-foreground">
              Registered learners
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Courses</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalCourses || 0}</div>
            <p className="text-xs text-muted-foreground">
              Active learning paths
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.completionRate || 0}%</div>
            <p className="text-xs text-muted-foreground">
              Overall progress
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Visual Dashboard Section - Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UserRoleDistributionChart
          adminCount={chartsData.userRoles.admin}
          staffCount={chartsData.userRoles.staff}
          userCount={chartsData.userRoles.user}
        />
        <CourseStatusDistributionChart
          notStarted={chartsData.courseStatus.notStarted}
          inProgress={chartsData.courseStatus.inProgress}
          completed={chartsData.courseStatus.completed}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopActiveCoursesChart data={chartsData.topCourses} />
        <UserEngagementChart data={chartsData.engagement} />
      </div>

      {/* Recent Activity and Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activities && activities.length > 0 ? (
                activities.slice(0, 10).map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 pb-3 border-b last:border-0">
                    <div className={`mt-1 p-1 rounded-full ${
                      activity.type === 'completion' ? 'bg-success/20' :
                      activity.type === 'achievement' ? 'bg-primary/20' :
                      activity.type === 'enrollment' ? 'bg-chart-2/20' :
                      activity.type === 'failure' ? 'bg-destructive/20' :
                      'bg-warning/20'
                    }`}>
                      {activity.type === 'completion' ? <Award className="h-3 w-3" /> :
                       activity.type === 'achievement' ? <Award className="h-3 w-3" /> :
                       activity.type === 'enrollment' ? <BookOpen className="h-3 w-3" /> :
                       activity.type === 'failure' ? <AlertCircle className="h-3 w-3" /> :
                       <Clock className="h-3 w-3" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{activity.user}</p>
                      <p className="text-xs text-muted-foreground truncate">{activity.action}</p>
                      <p className="text-xs text-muted-foreground">{activity.time}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No recent activity
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigateToTab('users', 'invite')}
              >
                <UserCheck className="mr-2 h-4 w-4" />
                Invite User
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigateToTab('users', 'approvals')}
              >
                <Users className="mr-2 h-4 w-4" />
                Approve Pending
                {pendingCount > 0 && (
                  <Badge variant="destructive" className="ml-auto">
                    {pendingCount}
                  </Badge>
                )}
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigateToTab('courses')}
              >
                <BookOpen className="mr-2 h-4 w-4" />
                Manage Courses
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigateToTab('analytics')}
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                View Analytics
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminOverviewTab;
